import { ArrowDown, ArrowUp, Lock, Plus, Search, SearchX } from 'lucide-react';
import { useId, useState, type ReactElement, type ReactNode, type SyntheticEvent } from 'react';

import {
  EmptyState,
  ErrorMessage,
  Loading,
  PageHeader,
  StepForm,
  type Step,
} from './components.js';
import { KeyFacts } from './fiche.js';
import { ActionBar, ConfirmDialog, Drawer, Flash, type useFlash } from './page.js';
import {
  ActionMenu,
  Kbd,
  Switch,
  Tabs,
  useHotkey,
  type ActionItem,
  type TabItem,
} from './patterns.js';
import { DataTable, type Column, type DataTableProps } from './table.js';

import './templates.css';

/**
 * Gabarits communs à tous les écrans (décision du référent, 0.4.0-rc.6) : une liste
 * (`ListPage`), une fiche (`FichePage`), une saisie dans un tiroir (`DrawerForm`) et une
 * saisie en étapes dans un tiroir (`StepDrawer`). Ils assemblent les patrons existants
 * (`PageHeader`, `ActionBar`, `Flash`, `DataTable`, `KeyFacts`, `Tabs`, `Drawer`,
 * `StepForm`), et une liste de paramètres complète (`ParamList`) : une action principale par page, les autres dans « Actions ▾ », tout
 * formulaire à la demande, un message de réussite à la fermeture. Exemples :
 * docs/ux/composants.md § Gabarits.
 */

type Flasher = ReturnType<typeof useFlash>;

/* ------------------------------------------------------------------ */
/* Liste                                                               */
/* ------------------------------------------------------------------ */

export interface ListPageProps<T> extends Omit<DataTableProps<T>, 'empty'> {
  title: string;
  description?: string | undefined;
  /** Action principale (bouton `variant="primary"`), ex. « Nouvelle activité ». */
  primary?: ReactNode;
  /** Actions secondaires (« Actions ▾ »). */
  more?: readonly (ActionItem | false | null | undefined)[];
  /** Recherche texte (toujours en premier). */
  search?:
    | { value: string; onChange: (value: string) => void; placeholder?: string; label?: string }
    | undefined;
  /** Deux filtres au plus (`Select`, `ChipFilter`…), à droite de la recherche. */
  filters?: ReactNode;
  /** Pastilles « à traiter » sous la barre (`ChipFilter`). */
  chips?: ReactNode;
  /** Un filtre ou une recherche masque des lignes : « Effacer les filtres ». */
  filtered?: boolean | undefined;
  onReset?: (() => void) | undefined;
  /** État vide sans filtre : phrase et bouton pour créer. */
  empty: { title: string; text?: ReactNode; action?: ReactNode };
  /** Message de réussite de la page (`useFlash`). */
  flash?: Flasher | undefined;
  loading?: boolean | undefined;
  error?: unknown;
  /** Contenu sous le tableau (pagination serveur, tiroirs). */
  children?: ReactNode;
}

/**
 * Page de liste : en-tête (titre, « Actions ▾ », action principale), recherche + 2 filtres +
 * nombre de résultats, pastilles, tableau dense (cartes au téléphone), état vide qui guide
 * (« Aucune activité à venir. [Nouvelle activité] ») ou « Effacer les filtres ».
 */
export function ListPage<T>({
  title,
  description,
  primary,
  more,
  search,
  filters,
  chips,
  filtered = false,
  onReset,
  empty,
  flash,
  loading = false,
  error,
  children,
  rows,
  ...table
}: ListPageProps<T>): ReactElement {
  const searchId = useId();
  const hasBar = search !== undefined || filters !== undefined;
  return (
    <div className="o-stack o-listpage">
      <PageHeader
        title={title}
        {...(description === undefined ? {} : { description })}
        actions={<ActionBar primary={primary} more={more ?? []} />}
      />
      {flash !== undefined && <Flash message={flash.message} onClose={flash.clear} />}
      {hasBar && (
        <div className="o-listbar" role="search" aria-label={`Filtrer : ${title}`}>
          {search !== undefined && (
            <div className="o-listbar__search">
              <label htmlFor={searchId} className="o-visually-hidden">
                {search.label ?? 'Rechercher'}
              </label>
              <Search aria-hidden="true" size={16} className="o-listbar__icon" />
              <input
                id={searchId}
                type="search"
                className="o-input"
                value={search.value}
                placeholder={search.placeholder ?? 'Rechercher…'}
                onChange={(e) => {
                  search.onChange(e.target.value);
                }}
              />
            </div>
          )}
          {filters !== undefined && <div className="o-listbar__filters">{filters}</div>}
          {!loading && error === undefined && (
            <span className="o-listbar__count" aria-live="polite">
              {rows.length} résultat{rows.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
      {chips}
      {loading && <Loading />}
      {error !== undefined && error !== null && <ErrorMessage error={error} />}
      {!loading && (error === undefined || error === null) && (
        <DataTable
          {...table}
          rows={rows}
          empty={
            filtered ? (
              <EmptyState
                title="Aucun résultat ne correspond à ces filtres."
                icon={SearchX}
                action={
                  onReset === undefined ? undefined : (
                    <button type="button" className="o-button" onClick={onReset}>
                      Effacer les filtres
                    </button>
                  )
                }
              />
            ) : (
              <EmptyState title={empty.title} action={empty.action}>
                {empty.text !== undefined && <p className="o-hint">{empty.text}</p>}
              </EmptyState>
            )
          }
        />
      )}
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fiche                                                               */
/* ------------------------------------------------------------------ */

export interface FichePageProps {
  title: string;
  /** Référence, dates, lieu : une ligne sous le titre. */
  subtitle?: string | undefined;
  /** Badges d'état (`StatusBadge`), sous le titre, jamais mêlés aux boutons. */
  badges?: ReactNode;
  /** Action principale (peut changer selon l'état, jamais d'emplacement). */
  primary?: ReactNode;
  more?: readonly (ActionItem | false | null | undefined)[];
  flash?: Flasher | undefined;
  /** « En bref » : faits clés (entrées `false` ignorées). */
  facts?: readonly ([string, ReactNode] | false | null | undefined)[];
  /** Onglets (6 au plus), état dans l'adresse (`?onglet=`), montés à la première visite. */
  tabs?: readonly (TabItem | false | null | undefined)[];
  tabsLabel?: string;
  /** Colonne « Lié à » (≥ 1 280 px ; dessous au-delà). Ni action ni formulaire. */
  aside?: ReactNode;
  loading?: boolean | undefined;
  error?: unknown;
  /** Contenu sans onglets, ou tiroirs et confirmations de la fiche. */
  children?: ReactNode;
}

/**
 * Page de fiche : en-tête (titre, ligne de référence, badges, « Actions ▾ » + principal),
 * message de réussite, « En bref », onglets ; « Lié à » à droite sur grand écran. Les
 * tiroirs (`DrawerForm`) et confirmations vont dans `children`.
 */
export function FichePage({
  title,
  subtitle,
  badges,
  primary,
  more,
  flash,
  facts,
  tabs,
  tabsLabel,
  aside,
  loading = false,
  error,
  children,
}: FichePageProps): ReactElement {
  if (loading) return <Loading />;
  if (error !== undefined && error !== null) return <ErrorMessage error={error} />;
  const shownFacts = (facts ?? []).filter(
    (f): f is [string, ReactNode] => f !== false && f !== null && f !== undefined,
  );
  const main = (
    <div className="o-stack o-fichepage__main">
      {shownFacts.length > 0 && (
        <section className="o-fichepage__facts" aria-label="En bref">
          <KeyFacts items={shownFacts} />
        </section>
      )}
      {tabs !== undefined && <Tabs tabs={tabs} label={tabsLabel ?? title} mount="visited" />}
      {children}
    </div>
  );
  return (
    <div className="o-stack o-fichepage">
      <PageHeader
        title={title}
        {...(subtitle === undefined ? {} : { description: subtitle })}
        actions={<ActionBar primary={primary} more={more ?? []} />}
      >
        {badges !== undefined && badges !== null && badges !== false ? (
          <div className="o-fichepage__badges">{badges}</div>
        ) : undefined}
      </PageHeader>
      {flash !== undefined && <Flash message={flash.message} onClose={flash.clear} />}
      {aside === undefined || aside === null || aside === false ? (
        main
      ) : (
        <div className="o-fiche">
          {main}
          <aside className="o-fiche__aside o-stack o-stack--tight" aria-label="Lié à">
            {aside}
          </aside>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Saisie dans un tiroir                                               */
/* ------------------------------------------------------------------ */

/**
 * Envoi d'un formulaire de tiroir : promesse (succès → fermeture + message ; erreur →
 * affichée au-dessus des boutons, tiroir ouvert, rien n'est perdu). Renvoyer `false` garde le
 * tiroir ouvert sans message (contrôle refusé).
 */
type DrawerSubmit = (data: FormData, event: SyntheticEvent<HTMLFormElement>) => unknown;

function useDrawerSubmit({
  onSubmit,
  onOpenChange,
  flash,
  success,
}: {
  onSubmit: DrawerSubmit;
  onOpenChange: (open: boolean) => void;
  flash?: Flasher | undefined;
  success?: string | undefined;
}): {
  pending: boolean;
  error: unknown;
  reset: () => void;
  submit: (event: SyntheticEvent<HTMLFormElement>) => void;
} {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const done = (result: unknown): void => {
    setPending(false);
    if (result === false) return;
    onOpenChange(false);
    if (success !== undefined) flash?.show(success);
  };
  return {
    pending,
    error,
    reset: () => {
      setError(null);
    },
    submit: (event) => {
      event.preventDefault();
      setError(null);
      let result: unknown;
      try {
        result = onSubmit(new FormData(event.currentTarget), event);
      } catch (e) {
        setError(e);
        return;
      }
      if (result instanceof Promise) {
        setPending(true);
        result.then(done, (e: unknown) => {
          setPending(false);
          setError(e);
        });
      } else done(result);
    },
  };
}

export interface DrawerFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string | undefined;
  /** Libellé qui répète l'action : « Enregistrer le plein ». */
  submitLabel: string;
  onSubmit: DrawerSubmit;
  /** Message affiché sous l'en-tête de la page après la fermeture : « Plein enregistré. » */
  success?: string | undefined;
  flash?: Flasher | undefined;
  /** Envoi en cours géré par l'écran (mutation) ; s'ajoute à celui du tiroir. */
  pending?: boolean | undefined;
  /** Erreur de l'écran (mutation) ; s'ajoute à celle du tiroir. */
  error?: unknown;
  danger?: boolean | undefined;
  wide?: boolean | undefined;
  /** Champs (`Field`, `TextInput`… en `FieldGrid`). Six au plus, sinon `StepDrawer`. */
  children: ReactNode;
}

/**
 * Tiroir de saisie : formulaire à droite (plein écran au téléphone), [Annuler]
 * [■ Enregistrer …] en bas à droite, erreur au-dessus des boutons, fermeture et message de
 * réussite quand l'envoi aboutit. Le contrôle natif des champs (`required`…) s'applique.
 */
export function DrawerForm({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  onSubmit,
  success,
  flash,
  pending: outerPending = false,
  error: outerError,
  danger = false,
  wide = false,
  children,
}: DrawerFormProps): ReactElement {
  const formId = useId();
  const { pending, error, reset, submit } = useDrawerSubmit({
    onSubmit,
    onOpenChange,
    flash,
    success,
  });
  const shownError = error ?? outerError;
  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title={title}
      description={description}
      wide={wide}
      footer={
        <>
          {shownError !== undefined && shownError !== null && (
            <div className="o-drawer__error">
              <ErrorMessage error={shownError} />
            </div>
          )}
          <button
            type="button"
            className="o-button"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            form={formId}
            className={danger ? 'o-button o-button--danger' : 'o-button o-button--primary'}
            disabled={pending || outerPending}
          >
            {pending || outerPending ? 'Enregistrement…' : submitLabel}
          </button>
        </>
      }
    >
      <form
        id={formId}
        className="o-drawer-form"
        onSubmit={(e) => {
          submit(e);
        }}
      >
        {children}
      </form>
    </Drawer>
  );
}

export interface StepDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string | undefined;
  /** 3 à 4 étapes nommées (« Quoi », « Quand et où », « Qui », « Vérifier »). */
  steps: Step[];
  submitLabel: string;
  onSubmit: DrawerSubmit;
  success?: string | undefined;
  flash?: Flasher | undefined;
  pending?: boolean | undefined;
  error?: unknown;
}

/**
 * Formulaire en étapes dans un tiroir large (création de 7 à 15 champs) : étapes en tête,
 * [Annuler] [Précédent] [■ Suivant / Enregistrer …] en bas, aucune saisie perdue entre les
 * étapes ; la dernière étape résume avant d'enregistrer. Mêmes règles d'envoi que
 * `DrawerForm`.
 */
export function StepDrawer({
  open,
  onOpenChange,
  title,
  description,
  steps,
  submitLabel,
  onSubmit,
  success,
  flash,
  pending: outerPending = false,
  error: outerError,
}: StepDrawerProps): ReactElement {
  const { pending, error, reset, submit } = useDrawerSubmit({
    onSubmit,
    onOpenChange,
    flash,
    success,
  });
  const shownError = error ?? outerError;
  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title={title}
      description={description}
      wide
    >
      <div className="o-stepdrawer">
        <StepForm
          steps={steps}
          submitLabel={pending || outerPending ? 'Enregistrement…' : submitLabel}
          pending={pending || outerPending}
          onSubmit={submit}
          onCancel={() => {
            reset();
            onOpenChange(false);
          }}
          footer={
            shownError !== undefined && shownError !== null ? (
              <ErrorMessage error={shownError} />
            ) : undefined
          }
        />
      </div>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/* Liste de paramètres                                                 */
/* ------------------------------------------------------------------ */

/** Élément utilisé, non supprimé (réponse 409 `in_use` de l'API) : nombre d'usages. */
function usagesDe(e: unknown): number | undefined {
  const r = e as { status?: number; body?: { code?: string; usages?: unknown } } | null;
  if (r?.status !== 409 || r.body?.code !== 'in_use') return undefined;
  return typeof r.body.usages === 'number' ? r.body.usages : 1;
}

export interface ParamListProps<T, D> {
  title: string;
  description?: string | undefined;
  /** Nom d'un élément, au singulier (« source ») : titres et messages. */
  noun: string;
  /** Article du nom : « Nouvelle source » / « Nouveau type ». */
  feminine?: boolean | undefined;
  rows: readonly T[];
  rowKey: (row: T) => string;
  /** Libellé d'un élément dans les messages (« Diffusion »). */
  rowLabel: (row: T) => string;
  columns: Column<T>[];
  loading?: boolean | undefined;
  error?: unknown;
  flash: Flasher;
  empty: { title: string; text?: ReactNode };
  /** Saisie : valeur vide (ajout), valeur d'une ligne (modification), champs du tiroir. */
  form: {
    empty: D;
    fromRow: (row: T) => D;
    /** `mode` : un champ figé après création se rend `disabled` en modification. */
    render: (
      draft: D,
      set: (patch: Partial<D>) => void,
      mode: 'ajout' | 'modification',
    ) => ReactNode;
  };
  /** Champs figés après création, avec leur raison (affichés dans le tiroir). */
  frozen?: readonly { label: string; reason: string }[];
  onCreate?: ((draft: D) => Promise<unknown>) | undefined;
  onUpdate?: ((row: T, draft: D) => Promise<unknown>) | undefined;
  /** Interrupteur « Actif » dans la ligne. */
  active?: { get: (row: T) => boolean; set: (row: T, active: boolean) => Promise<unknown> };
  /** Monter / descendre (liste ordonnée). */
  move?: ((row: T, direction: -1 | 1) => Promise<unknown>) | undefined;
  /** Suppression ; refusée par l'API (409 `in_use`) : proposition de désactiver. */
  onDelete?: ((row: T) => Promise<unknown>) | undefined;
  /** Pourquoi une ligne ne se supprime pas d'avance (usages connus) ; `undefined` : permis. */
  deleteBlocked?: ((row: T) => string | undefined) | undefined;
  /** Actions de page supplémentaires (« Actions ▾ »). */
  more?: readonly (ActionItem | false | null | undefined)[];
}

/**
 * Liste de paramètres complète (docs/etudes/reglages-audit.md § 3.3, demande du référent du
 * 2026-11-04 : « on ne peut pas modifier des valeurs déjà présentes ») : ajouter, modifier
 * (clic sur la ligne ou « ⋯ »), activer / désactiver d'un interrupteur, réordonner, supprimer
 * avec confirmation. Un élément utilisé n'est pas supprimé : l'API répond 409 avec le nombre
 * d'usages et la liste propose de le désactiver. Les champs figés après création sont
 * affichés avec leur raison.
 */
export function ParamList<T, D>({
  title,
  description,
  noun,
  feminine = false,
  rows,
  rowKey,
  rowLabel,
  columns,
  loading = false,
  error,
  flash,
  empty,
  form,
  frozen = [],
  onCreate,
  onUpdate,
  active,
  move,
  onDelete,
  deleteBlocked,
  more,
}: ParamListProps<T, D>): ReactElement {
  const [editing, setEditing] = useState<{ row: T | null; draft: D } | null>(null);
  const [deleting, setDeleting] = useState<T | null>(null);
  const [inUse, setInUse] = useState<{ row: T; usages: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const nouveau = `${feminine ? 'Nouvelle' : 'Nouveau'} ${noun}`;
  // « N » : nouvel élément, comme sur les autres listes.
  useHotkey(
    'n',
    () => {
      setEditing({ row: null, draft: form.empty });
    },
    onCreate !== undefined && editing === null,
  );
  const run = async (work: () => Promise<unknown>, success: string): Promise<boolean> => {
    setBusy(true);
    setActionError(null);
    try {
      await work();
      flash.show(success);
      return true;
    } catch (e) {
      setActionError(e);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const rowActions = (r: T, blocked: string | undefined): ActionItem[] => {
    const items: ActionItem[] = [];
    if (onUpdate !== undefined)
      items.push({
        label: 'Modifier',
        onSelect: () => {
          setEditing({ row: r, draft: form.fromRow(r) });
        },
      });
    if (onDelete !== undefined)
      items.push('separator', {
        label: blocked === undefined ? 'Supprimer' : `Supprimer (${blocked})`,
        danger: true,
        disabled: blocked !== undefined,
        onSelect: () => {
          setDeleting(r);
        },
      });
    return items;
  };
  const extra: Column<T>[] = [
    ...(active === undefined
      ? []
      : [
          {
            key: 'actif',
            header: 'Actif',
            sortValue: (r: T) => active.get(r),
            render: (r: T) => (
              <Switch
                checked={active.get(r)}
                label={`${rowLabel(r)} : actif`}
                disabled={busy}
                onChange={(on) => {
                  void run(
                    () => active.set(r, on),
                    `${rowLabel(r)} : ${on ? 'activé' : 'désactivé'}.`,
                  );
                }}
              />
            ),
          },
        ]),
    {
      key: 'actions',
      header: 'Actions',
      align: 'end',
      render: (r: T) => {
        const i = rows.indexOf(r);
        const blocked = deleteBlocked?.(r);
        return (
          <span className="o-paramlist__actions">
            {move !== undefined && (
              <>
                <button
                  type="button"
                  className="o-button o-button--ghost o-icon-button"
                  aria-label={`Monter ${rowLabel(r)}`}
                  disabled={busy || i <= 0}
                  onClick={() => {
                    void run(() => move(r, -1), `${rowLabel(r)} monté.`);
                  }}
                >
                  <ArrowUp aria-hidden="true" size={16} />
                </button>
                <button
                  type="button"
                  className="o-button o-button--ghost o-icon-button"
                  aria-label={`Descendre ${rowLabel(r)}`}
                  disabled={busy || i >= rows.length - 1}
                  onClick={() => {
                    void run(() => move(r, 1), `${rowLabel(r)} descendu.`);
                  }}
                >
                  <ArrowDown aria-hidden="true" size={16} />
                </button>
              </>
            )}
            <ActionMenu
              iconOnly
              label={`Actions : ${rowLabel(r)}`}
              items={rowActions(r, blocked)}
            />
          </span>
        );
      },
    },
  ];
  const editable = columns.map((c, n) =>
    n === 0 && onUpdate !== undefined && c.render !== undefined
      ? {
          ...c,
          render: (r: T) => (
            <button
              type="button"
              className="o-link-button"
              onClick={() => {
                setEditing({ row: r, draft: form.fromRow(r) });
              }}
            >
              {c.render?.(r)}
            </button>
          ),
        }
      : c,
  );
  const mode = editing?.row === null ? 'ajout' : 'modification';
  return (
    <>
      <ListPage
        title={title}
        description={description}
        primary={
          onCreate === undefined ? undefined : (
            <button
              type="button"
              className="o-button o-button--primary"
              onClick={() => {
                setEditing({ row: null, draft: form.empty });
              }}
            >
              <Plus aria-hidden="true" size={18} /> {nouveau} <Kbd keys="n" />
            </button>
          )
        }
        more={more ?? []}
        flash={flash}
        loading={loading}
        error={error ?? (actionError === null ? undefined : actionError)}
        caption={title}
        columns={[...editable, ...extra]}
        rows={rows}
        rowKey={rowKey}
        empty={{
          title: empty.title,
          text: empty.text,
          action:
            onCreate === undefined ? undefined : (
              <button
                type="button"
                className="o-button o-button--primary"
                onClick={() => {
                  setEditing({ row: null, draft: form.empty });
                }}
              >
                {nouveau}
              </button>
            ),
        }}
      />
      {editing !== null && (
        <DrawerForm
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          title={
            mode === 'ajout'
              ? nouveau
              : `Modifier : ${editing.row === null ? '' : rowLabel(editing.row)}`
          }
          submitLabel={mode === 'ajout' ? `Créer ${feminine ? 'la' : 'le'} ${noun}` : 'Enregistrer'}
          flash={flash}
          success={
            mode === 'ajout'
              ? `${noun.charAt(0).toUpperCase()}${noun.slice(1)} créé${feminine ? 'e' : ''}.`
              : 'Modifications enregistrées.'
          }
          onSubmit={() =>
            editing.row === null
              ? onCreate?.(editing.draft)
              : onUpdate?.(editing.row, editing.draft)
          }
        >
          {mode === 'modification' && frozen.length > 0 && (
            <ul className="o-paramlist__frozen">
              {frozen.map((f) => (
                <li key={f.label}>
                  <Lock aria-hidden="true" size={14} /> <strong>{f.label}</strong> : {f.reason}
                </li>
              ))}
            </ul>
          )}
          {form.render(
            editing.draft,
            (patch) => {
              setEditing((e) => (e === null ? e : { ...e, draft: { ...e.draft, ...patch } }));
            },
            mode,
          )}
        </DrawerForm>
      )}
      {deleting !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setDeleting(null);
          }}
          title={`Supprimer ${rowLabel(deleting)} ?`}
          confirmLabel="Supprimer"
          danger
          pending={busy}
          onConfirm={() => {
            const row = deleting;
            setBusy(true);
            setActionError(null);
            Promise.resolve(onDelete?.(row))
              .then(() => {
                flash.show(`${rowLabel(row)} supprimé.`);
                setDeleting(null);
              })
              .catch((e: unknown) => {
                const usages = usagesDe(e);
                setDeleting(null);
                if (usages !== undefined) setInUse({ row, usages });
                else setActionError(e);
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          <p>La suppression est définitive. Elle est inscrite au journal des actions.</p>
        </ConfirmDialog>
      )}
      {inUse !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setInUse(null);
          }}
          title={`${rowLabel(inUse.row)} est utilisé`}
          confirmLabel={active === undefined ? 'Compris' : 'Désactiver'}
          pending={busy}
          onConfirm={() => {
            const { row } = inUse;
            setInUse(null);
            if (active !== undefined)
              void run(() => active.set(row, false), `${rowLabel(row)} désactivé.`);
          }}
        >
          <p>
            {inUse.usages} usage{inUse.usages > 1 ? 's' : ''} : il n&apos;est pas supprimé.
            {active === undefined ? '' : ' Le désactiver le retire sans rien perdre.'}
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}

/** Élément à supprimer par `useSuppression`. */
export interface SuppressionTarget {
  label: string;
  remove: () => Promise<unknown>;
  /** Repli si l'élément est utilisé (« Archiver », « Désactiver ») ; absent : « Compris ». */
  fallback?: { label: string; run: () => Promise<unknown> } | undefined;
}

/**
 * Suppression avec garde-fou pour les listes qui gardent leur propre éditeur (Support, Crise,
 * Socle…) : même règle que `ParamList` — confirmation, puis, si l'API répond 409 `in_use`,
 * proposition d'archiver (ou de désactiver) à la place, sans rien perdre. `ask` ouvre la
 * confirmation ; `dialogs` se rend une fois dans la page.
 */
export function useSuppression(flash: Flasher): {
  ask: (target: SuppressionTarget) => void;
  error: unknown;
  dialogs: ReactElement;
} {
  type Target = SuppressionTarget;
  const [deleting, setDeleting] = useState<Target | null>(null);
  const [inUse, setInUse] = useState<{ target: Target; usages: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const dialogs = (
    <>
      {deleting !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setDeleting(null);
          }}
          title={`Supprimer ${deleting.label} ?`}
          confirmLabel="Supprimer"
          danger
          pending={busy}
          onConfirm={() => {
            const target = deleting;
            setBusy(true);
            setError(null);
            target
              .remove()
              .then(() => {
                flash.show(`${target.label} supprimé.`);
                setDeleting(null);
              })
              .catch((e: unknown) => {
                const usages = usagesDe(e);
                setDeleting(null);
                if (usages !== undefined) setInUse({ target, usages });
                else setError(e);
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          <p>La suppression est définitive. Elle est inscrite au journal des actions.</p>
        </ConfirmDialog>
      )}
      {inUse !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setInUse(null);
          }}
          title={`${inUse.target.label} est utilisé`}
          confirmLabel={inUse.target.fallback?.label ?? 'Compris'}
          pending={busy}
          onConfirm={() => {
            const { target } = inUse;
            setInUse(null);
            const fallback = target.fallback;
            if (fallback === undefined) return;
            setBusy(true);
            fallback
              .run()
              .then(() => {
                flash.show(`${target.label} : ${fallback.label.toLowerCase()}, rien n'est perdu.`);
              })
              .catch((e: unknown) => {
                setError(e);
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          <p>
            {inUse.usages} usage{inUse.usages > 1 ? 's' : ''} : il n&apos;est pas supprimé.
            {inUse.target.fallback === undefined
              ? ''
              : ` « ${inUse.target.fallback.label} » le retire sans rien perdre.`}
          </p>
        </ConfirmDialog>
      )}
    </>
  );
  return {
    ask: (target) => {
      setDeleting(target);
    },
    error,
    dialogs,
  };
}
